"use client";

import { cn } from "@/lib/utils";
import { isCmsShowFlagOn } from "@/utils/variantHelpers";
import Link from "next/link";
import { useMemo } from "react";

/**
 * Footer 5014 — KUK footer.
 *
 * Port of kuk-campus-portal `components/organisms/Footer.tsx`, specifically its
 * landing-page branch: a solid navy band with an optional row of link columns and
 * a centred copyright bar.
 *
 * BREAKPOINTS — the v1 source is built on Tailwind's DEFAULT scale, where `md:`
 * is 768px, plus `lg:` (1024px) on the copyright bar's padding and type size.
 * This app remaps `md:` to 1230px and exposes 768px as `tab:` (see globals.css
 * `--breakpoint-*`), so the reference's `md:` becomes `tab:` and its `lg:`
 * becomes this app's `sm:` (1080px), the nearest real breakpoint.
 *
 * THE LP BRANCH IS THE DEFAULT. v1 decides what to render by sniffing the URL —
 * `pathname.includes("-lp")` hides the About Us column, the social icons and the
 * disclaimer. A widget cannot know that and should not guess: every part is a
 * CMS toggle here, so the same variant serves a landing page (columns off) and a
 * full site footer (columns on) without inspecting the route.
 *
 * DROPPED — v1's hardcoded social links (three fixed Facebook / Instagram /
 * YouTube URLs pointing at specific KUK accounts) and its `link1`…`link9` /
 * `link1_title`…`link9_title` column pairs, capped at nine and indexed by
 * position. Both become ordinary footer link columns, which this app already
 * models as `footer_category` → `footer_item` with no cap.
 *
 * THE COPYRIGHT BAR carries TWO stacked lines in the reference, at the same type
 * scale: the disclaimer first, then the copyright sentence with an inline policy
 * link after it. An earlier pass here put the disclaimer in the block ABOVE the
 * bar (where it renders at a different size, and only when link columns are
 * shown) and dropped the link entirely for want of a column. Both are fixed:
 * the disclaimer is the bar's first line, and `privacy_link_label` /
 * `privacy_link_url` model the link — see the 20260901230000 migration for why
 * a `footer_item` cannot express an inline link inside a sentence.
 *
 * THE HEADING RULE is v1's `.footer-underline` — a stylesheet pseudo-element, not
 * markup, which is why the first pass missed it: a 3px bar the width of the
 * heading, `#00aded` at 40% opacity, sitting 4px below the text. Rendered here as
 * a real span so the colour can come from the CMS inline (see
 * `heading_underline_color`). v1 re-centres it under 768px with
 * `left:50%; translateX(-50%)`; that is a no-op in this layout — the heading is
 * shrink-to-fit in a centred flex column either way — so `left-0 w-full` covers
 * both breakpoints.
 */

interface FooterLink {
  id?: number;
  label?: string;
  label_color?: string;
  url?: string;
  priority?: number;
  is_active?: boolean;
}

interface FooterCategory {
  id?: number;
  name?: string;
  name_text_color?: string;
  order_index?: number;
  status?: boolean;
  is_active?: boolean;
  items?: FooterLink[];
}

interface Footer5014Data {
  status?: boolean | number | string;
  categories?: FooterCategory[];
  /** Band fill, or the first stop when both gradient colours are set. */
  background_color?: string;
  gradient_start_color?: string;
  gradient_end_color?: string;
  /** Link text. */
  text_color?: string;
  /** Accent rule under each column heading. Empty = no rule. */
  heading_underline_color?: string;
  /** Show the link columns at all. */
  enable_footer_content?: boolean | number | string;
  /** Disclaimer — the first line of the copyright bar. */
  title?: string;
  title_text_color?: string;
  /** Inline policy link, appended to the copyright sentence. */
  privacy_link_label?: string;
  privacy_link_url?: string;
  /** Copyright bar. */
  enable_copyright?: boolean | number | string;
  copyright_text?: string;
  copyright_text_color?: string;
  /** Columns at `tab` and up. */
  column_count?: number;
}

/**
 * A nullable CMS column, not a string: these come straight from MySQL, where an
 * unset URL is NULL — and a destructuring default only fills in `undefined`, so
 * `null` reaches here intact.
 */
function normalizeHref(href: string | null | undefined): string {
  const h = (href ?? "").trim();
  if (!h) return "#";
  if (
    h.startsWith("http://") ||
    h.startsWith("https://") ||
    h.startsWith("/") ||
    h.startsWith("#") ||
    h.startsWith("mailto:") ||
    h.startsWith("tel:")
  ) {
    return h;
  }
  return `/${h.replace(/^\//, "")}`;
}

const Footer5014 = (props: { data?: Footer5014Data }) => {
  const { data: raw } = props;
  const sectionData = ((raw as any)?.data ?? raw) as Footer5014Data | undefined;

  const {
    background_color = "#031F44",
    gradient_start_color = "",
    gradient_end_color = "",
    text_color = "#FFFFFF",
    heading_underline_color = "#00aded",
    title = "",
    title_text_color = "#FFFFFF",
    copyright_text = "",
    copyright_text_color = "#FFFFFF",
    privacy_link_label = "",
    privacy_link_url = "",
    column_count = 3,
  } = sectionData || {};

  const status = isCmsShowFlagOn(sectionData?.status, true);
  const showColumns = isCmsShowFlagOn(sectionData?.enable_footer_content, true);
  const showCopyright = isCmsShowFlagOn(sectionData?.enable_copyright, true);

  const categories = useMemo(() => {
    const cats = sectionData?.categories;
    if (!Array.isArray(cats)) return [];
    return cats
      .filter((c) => c?.status !== false && c?.is_active !== false)
      .sort((a, b) => (Number(a?.order_index) || 0) - (Number(b?.order_index) || 0))
      .map((c) => ({
        ...c,
        items: (Array.isArray(c.items) ? c.items : [])
          .filter((i) => i?.is_active !== false && i?.label)
          .sort((a, b) => (Number(a?.priority) || 0) - (Number(b?.priority) || 0)),
      }))
      .filter((c) => c.name || c.items.length > 0);
  }, [sectionData?.categories]);

  if (!status) return null;

  const hasColumns = showColumns && categories.length > 0;
  const hasBar = showCopyright && Boolean(copyright_text || title);
  // Nothing to show at all — better to render nothing than an empty navy band.
  if (!hasColumns && !hasBar) return null;

  /* Both stops or neither: a single stop is not a gradient, and falling back to
     the flat colour keeps a half-configured row rendering a valid band. */
  const useGradient = Boolean(gradient_start_color && gradient_end_color);
  const underlineColor = (heading_underline_color || "").trim();
  const cols = Math.min(Math.max(Number(column_count) || 3, 1), 4);

  return (
    <footer
      className="w-full"
      style={{
        backgroundColor: background_color,
        ...(useGradient
          ? {
              backgroundImage: `linear-gradient(to right, ${gradient_start_color}, ${gradient_end_color})`,
            }
          : {}),
      }}
    >
      {hasColumns ? (
        <div className="flex items-center justify-center">
          <div className="w-full max-w-[1120px] px-10 py-10 tab:px-20">
            {categories.length > 0 ? (
              <div
                className={cn(
                  "grid grid-cols-1 gap-20 text-center tab:text-left",
                  cols === 1 && "tab:grid-cols-1",
                  cols === 2 && "tab:grid-cols-2",
                  cols === 3 && "tab:grid-cols-3",
                  cols === 4 && "tab:grid-cols-4",
                )}
              >
                {categories.map((cat, i) => (
                  <div
                    key={cat.id ?? i}
                    className="flex flex-col items-center justify-center space-y-4 tab:items-start tab:justify-start"
                  >
                    {cat.name ? (
                      <h3
                        className="relative text-2xl font-semibold"
                        style={{ color: cat.name_text_color || text_color }}
                      >
                        {cat.name}
                        {underlineColor ? (
                          /* v1 paints the bar at full strength and fades the
                             whole pseudo-element, so the band shows through it;
                             kept as `opacity` rather than pre-blended into the
                             hex, which would only be right over one band colour. */
                          <span
                            aria-hidden="true"
                            className="pointer-events-none absolute bottom-[-4px] left-0 h-[3px] w-full"
                            style={{ backgroundColor: underlineColor, opacity: 0.4 }}
                          />
                        ) : null}
                      </h3>
                    ) : null}
                    {cat.items.map((item, j) => (
                      <Link
                        key={item.id ?? j}
                        href={normalizeHref(item.url || "")}
                        className="text-xs font-extralight hover:underline"
                        style={{ color: item.label_color || text_color }}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {hasBar ? (
        /* v1: `flex-col items-center gap-2`, both lines at the same scale. */
        <div className="flex w-full flex-col items-center justify-center gap-2 px-5 py-6 sm:px-10 sm:py-9">
          {title ? (
            <p
              className="text-center text-xs font-normal sm:text-lg"
              style={{ color: title_text_color }}
            >
              {title}
            </p>
          ) : null}
          {copyright_text ? (
            <p
              className="text-center text-xs font-normal sm:text-lg"
              style={{ color: copyright_text_color }}
            >
              {copyright_text}
              {privacy_link_label ? (
                <>
                  {" "}
                  <Link
                    href={normalizeHref(privacy_link_url)}
                    className="underline-offset-2 hover:underline"
                  >
                    {privacy_link_label}
                  </Link>
                </>
              ) : null}
            </p>
          ) : null}
        </div>
      ) : null}
    </footer>
  );
};

export default Footer5014;
